import useMutation from '@hooks/useGQLMutation'
import { useModalReload } from '@hooks/useModalReload'
import { useAlertsStore } from '@stores/alertsStore'
import { PrioritiseSupporterDocument } from '@frontend/graphql/mutations/prioritiseSupporter.generated'
import { scopedTranslation } from '@frontend/utils/I18n'
import { toast } from '@stores/toastStore'

const t = scopedTranslation('views.calling_sessions.view_all_supporters')

type UsePrioritiseSupporterOptions = {
  alertsPath: string
  reloadProps?: string[]
}

type PrioritiseOptions = {
  callingSessionSupporterId: string
  supporterName: string
}

export function usePrioritiseSupporter({
  alertsPath,
  reloadProps = ['sessionSupporters', 'nextSupporterToCallId', 'callingSessionSupporter'],
}: UsePrioritiseSupporterOptions) {
  const { setAlerts } = useAlertsStore(({ setAlerts }) => ({ setAlerts }))
  const reload = useModalReload()
  const prioritiseMutation = useMutation(PrioritiseSupporterDocument)

  const prioritise = async ({ callingSessionSupporterId, supporterName }: PrioritiseOptions) => {
    try {
      const response = await prioritiseMutation.mutateAsync({ callingSessionSupporterId })

      if (response?.prioritiseSupporter?.success) {
        toast.success(t('alerts.call_next_success', { name: supporterName }))
        reload({ only: reloadProps })
        return true
      } else {
        const errors = response?.prioritiseSupporter?.errors || []
        setAlerts({
          alertsPath,
          alerts: errors.map((error: { message: string }) => ({
            type: 'error' as const,
            details: { title: error.message },
          })),
        })
        return false
      }
    } catch (error) {
      console.error('Prioritise supporter mutation failed:', error)
      setAlerts({
        alertsPath,
        alerts: [
          {
            type: 'error',
            details: { title: t('alerts.call_next_error') },
          },
        ],
      })
      return false
    }
  }

  return {
    prioritise,
    isLoading: prioritiseMutation.isLoading,
    error: prioritiseMutation.error,
  }
}
